import PartyPopperIcon from '@/assets/icons/party_popper.png';
import { HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { TierProgressBar } from '@/pages/CustomerDashboard/TierProgressBar';
import { ApiEnergyDashboard, ApiEnergyDashboardBehavior } from '@api/interfaces/ApiEnergyDashboard';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { alpha, Stack, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface EnergyProgressSectionProps {
  data: ApiEnergyDashboard,
}

export const EnergyBehaviorItem = ({ behavior }: { behavior: ApiEnergyDashboardBehavior }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Stack data-testid={`energy-behavior-${behavior.id}`} direction="row" gap="12px" py="8px">
      {behavior.completed
        ? <CheckCircle sx={{ color: theme.palette.success.main }} />
        : <RadioButtonUnchecked sx={{ color: theme.palette.grey[500] }} />}
      <Stack>
        <Text
          category="body-large"
          sx={{ textDecoration: behavior.completed ? 'line-through' : undefined }}
        >
          {behavior.name}
        </Text>
        <Text category="body-small" sx={{ color: theme.palette.grey[800] }}>
          {behavior.description}
        </Text>
        {behavior.completed && !!behavior.completedDate && (
          <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
            {t(($) => $.customerDashboard.energy.completedOn, {
              date: behavior.completedDate,
            })}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

const TopTier = ({ multiplier }: { multiplier: number }) => {
  const { t } = useTranslation();
  return (
    <Stack alignItems="center" data-testid="energy-top-tier">
      <Stack alignItems="center" borderRadius="12px" direction="row" p="4px 12px">
        <img src={PartyPopperIcon} style={{ height: '18px', width: '18px' }} />
        <HSpacer size="3" />
        <Text category="label-large">{t(($) => $.customerDashboard.energy.topTier)}</Text>
      </Stack>
      <VSpacer size="2" />
      <Text category="body-medium">
        {t(($) => $.customerDashboard.energy.topTierRate, { multiplier })}
      </Text>
    </Stack>
  );
};

export const EnergyProgressSection = ({ data }: EnergyProgressSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const nextRank = data.nextTier?.rank ?? Number.MAX_SAFE_INTEGER;
  const upcomingBehaviors = data.behaviors.filter(({ tierRank }) => tierRank <= nextRank);
  const laterBehaviors = data.behaviors.filter(({ tierRank }) => tierRank > nextRank);

  return (
    <Stack data-testid="energy-progress" p="24px 16px 0px 16px">
      <Text category="headline-medium">{t(($) => $.customers.progress.title)}</Text>
      <VSpacer size="5" />
      <Stack alignItems="center">
        <TierProgressBar
          rankCurrent={data.currentTier?.rank ?? null}
          tierNames={data.tiers.map(({ name }) => name)}
        />
      </Stack>
      <VSpacer size="5" />
      {data.isTopTier || !data.nextTier ? (
        <TopTier multiplier={data.currentTier?.pointsMultiplier ?? 1} />
      ) : (
        <Stack>
          <Text category="overline" testID="energy-next-tier">
            {t(($) => $.customerDashboard.energy.toReach, { tierName: data.nextTier.name })}
          </Text>
          <VSpacer size="1" />
          <Stack
            bgcolor={alpha(theme.palette.success.main, .12)}
            borderRadius="12px"
            maxWidth="fit-content"
            p="4px 12px"
          >
            <Text category="body-small" testID="energy-behaviors-remaining">
              {t(($) => $.customerDashboard.energy.behaviorsRemaining, {
                count: data.behaviorsRemainingForNextTier,
              })}
            </Text>
          </Stack>
          {!!data.missingRequiredBehaviorNames.length && (
            <>
              <VSpacer size="2" />
              <Text category="body-small" testID="energy-required-behaviors">
                {t(($) => $.customerDashboard.energy.required, {
                  names: data.missingRequiredBehaviorNames.join(', '),
                })}
              </Text>
            </>
          )}
        </Stack>
      )}
      <VSpacer size="5" />
      {upcomingBehaviors.map((behavior) => (
        <EnergyBehaviorItem behavior={behavior} key={behavior.id} />
      ))}
      {!!laterBehaviors.length && (
        <>
          <VSpacer size="4" />
          <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
            {t(($) => $.customerDashboard.energy.laterTiers)}
          </Text>
          {laterBehaviors.map((behavior) => (
            <EnergyBehaviorItem behavior={behavior} key={behavior.id} />
          ))}
        </>
      )}
    </Stack>
  );
};
